import API_BASE from './config';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

const TheaterMode = () => {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [scene, setScene] = useState(0); 

    useEffect(() => {
        const fetchData = () => {
            axios.get(`${API_BASE}/api/events/${id}/theater_mode/`)
                .then(res => setData(res.data))
                .catch(err => console.error("THEATER NODE OFFLINE", err));
        };
        fetchData();
        const interval = setInterval(fetchData, 12000); // SYNCED TO 12s
        return () => clearInterval(interval);
    }, [id]);

    useEffect(() => {
        if (!data) return;
        
        // FLOW: 
        // Scene 0: Key Event Banner
        // Scene 1: Live Attendance
        // Scene 2+: Sponsors
        const totalScenes = 2 + (data.sponsors?.length || 0);

        const timer = setInterval(() => {
            setScene(prev => (prev + 1) % totalScenes);
        }, 12000); // UPDATED TO 12 SECONDS

        return () => clearInterval(timer);
    }, [data]);

    // NEW: FULLSCREEN TOGGLE LOGIC
    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch((err) => {
                console.error(`Error enabling full-screen: ${err.message}`);
            });
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
    };

    if (!data) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-sky-500 font-black uppercase tracking-[1em] animate-pulse">Initializing EventDeck Node...</div>;

    const renderScene = () => {
        // --- 1. KEY BANNER ---
        if (scene === 0) {
            return (
                <div className="animate-in fade-in zoom-in-95 duration-1000 flex flex-col items-center w-full h-full justify-center p-10">
                    {data.event_banner ? (
                        <div className="w-full h-full flex items-center justify-center">
                            <img 
                                src={data.event_banner} 
                                className="max-w-[90%] max-h-[85vh] rounded-[3rem] shadow-[0_0_100px_rgba(14,165,233,0.1)] border border-white/5 object-contain" 
                                alt="Key Banner" 
                            />
                        </div>
                    ) : (
                        <h1 className="text-9xl font-black uppercase tracking-tighter italic">{data.event_name}</h1>
                    )}
                </div>
            );
        }

        // --- 2. LIVE ATTENDANCE ---
        if (scene === 1) {
            return (
                <div className="animate-in slide-in-from-bottom-12 duration-1000 flex flex-col items-center">
                    <p className="text-sky-500 text-3xl font-black uppercase tracking-[0.6em] mb-12">Live Check-Ins</p>
                    <h2 className="text-[28vw] font-black leading-none tracking-tighter text-white tabular-nums drop-shadow-[0_0_80px_rgba(255,255,255,0.05)]">
                        {data.live_attendance}
                    </h2>
                    <div className="mt-16 flex items-center gap-6 bg-white/[0.03] px-12 py-6 rounded-3xl border border-white/10 backdrop-blur-md">
                        <div className="w-5 h-5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_20px_rgba(16,185,129,0.5)]"></div>
                        <span className="text-2xl font-black uppercase tracking-[0.2em] text-gray-400">Infrastructure Synced • EventDeck</span>
                    </div>
                </div>
            );
        }

        // --- 3. SPONSORS (CINEMATIC FLOW) ---
        const sponsorIdx = scene - 2;
        const currentSponsor = data.sponsors[sponsorIdx];

        return (
            <div className="animate-in fade-in zoom-in-105 duration-1000 w-full h-full flex items-center justify-center p-10">
                {currentSponsor?.ad_banner ? (
                    <div className="relative w-full h-full flex items-center justify-center">
                        <img 
                            src={currentSponsor.ad_banner} 
                            className="max-w-full max-h-full rounded-[4rem] shadow-[0_0_100px_rgba(0,0,0,0.5)] border border-white/5 object-contain" 
                            alt={currentSponsor.name} 
                        />
                        <div className="absolute top-10 left-10 bg-black/60 backdrop-blur-xl px-6 py-3 rounded-2xl border border-white/10">
                            <p className="text-[10px] font-black text-sky-500 uppercase tracking-widest">Official Partner</p>
                            <p className="text-lg font-bold uppercase">{currentSponsor.name}</p>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center">
                        <img src={currentSponsor.logo} className="h-72 object-contain bg-white rounded-[3rem] p-12 mb-10 shadow-2xl" alt="" />
                        <p className="text-5xl font-black uppercase tracking-widest">{currentSponsor.name}</p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="h-screen w-screen bg-[#020202] text-white overflow-hidden relative font-sans">
            {/* AMBIENT BACKGROUND GLOW */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-sky-600/5 rounded-full blur-[150px]"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-900/5 rounded-full blur-[150px]"></div>
            </div>

            {/* MAIN STAGE */}
            <div className="relative h-full w-full flex flex-col items-center justify-center">
                {renderScene()}
            </div>

            {/* PERSISTENT BRANDING (CLICK TO FULLSCREEN) */}
            <div 
                onClick={toggleFullScreen}
                className="absolute bottom-12 left-12 flex items-center gap-4 opacity-20 hover:opacity-100 cursor-pointer transition-all active:scale-90 z-50"
            >
                <img
                    src="/logo192.png"
                    alt="EventDeck"
                    className="w-10 h-10 object-contain"
                />
                <span className="font-black uppercase tracking-tighter text-2xl">EVENTDECK</span>
            </div>
            
            <div className="absolute bottom-12 right-12 opacity-20 text-[10px] font-black uppercase tracking-[0.5em] text-right">
                Global Intelligence Node<br/>
                <span className="text-sky-500">Live Sequence Active • 2026</span>
            </div>
        </div>
    );
};

export default TheaterMode;